import { Component, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { BuildingView, CompanyBuildingView, CompanyView, SearchBuildingsForCompanyParameters } from '@api-proxies';
import { IKebabAction } from '@app/components/shared/grids/custom-cell-actions/custom-cell-actions.component';
import { TypeAheadBuildingsComponent } from '@app/components/shared/type-ahead-buildings/type-ahead-buildings.component';
import { GridService } from '@app/services/grid.service';
import { BuildingService } from '@http/clients/building.service';
import { CompanyBuildingService } from '@http/clients/company-building.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { AwaiterService } from '@services/awaiter.service';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ModalEditAddressComponent } from '@shared/modals/modal-edit-address/modal-edit-address.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { COMPANY_BUILDINGS_GRID_COLUMNS } from './companies-single-buildings.config';

@Component({
    selector: 'app-companies-single-buildings',
    templateUrl: './companies-single-buildings.component.html',
    styleUrls: ['./companies-single-buildings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, TypeAheadBuildingsComponent]
})
export class CompaniesSingleBuildingsComponent {
    company = input<CompanyView>();

    tabCounterChange = output<void>();

    handleCompanyChange = output<void>();

    buildingsDatasource?: IServerSideDatasource;
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    gridColumns: ColDef[] = [...COMPANY_BUILDINGS_GRID_COLUMNS];
    typeAheadSearchByEntityParameters: ITypeAheadDropdownEntityParameters = {
        entityKeyName: EntityTypes.Company
    };

    private readonly buildingService = inject(BuildingService);
    private readonly companyBuildingService = inject(CompanyBuildingService);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly gridService = inject(GridService);

    constructor() {
        this.setupGridColumns();
        effect(() => {
            const companyId = this.company()?.id;
            const organizationId = this.company()?.organizationId;

            if (companyId) {
                this.typeAheadSearchByEntityParameters = {
                    entityKeyName: EntityTypes.Company,
                    entity: {
                        companyId,
                        organizationId
                    }
                };
            }

            if (companyId && !this.buildingsDatasource) {
                this.createDatasource();
            }
        });
    }

    getRowIdFn = (params: any) => params.data.buildingId.toString();

    async editAddress(companyBuilding: CompanyBuildingView): Promise<void> {
        const updatedCompanyBuilding = (await this.modalService.open(ModalEditAddressComponent, {
            companyBuilding: companyBuilding
        })) as CompanyBuildingView | undefined;

        if (updatedCompanyBuilding) {
            const gridApi = this.gridService.getGridApi();
            if (gridApi) {
                const node = gridApi.getRowNode(updatedCompanyBuilding.buildingId.toString());
                if (node?.data) {
                    Object.assign(node.data, updatedCompanyBuilding);
                    gridApi.redrawRows({ rowNodes: [node] });
                }
            }
            this.handleCompanyChange.emit();
        }
    }

    async setAsMainOffice(companyBuilding: CompanyBuildingView) {
        if (!companyBuilding) return;
        const company = this.company();
        if (!company) return;

        if (company.mainOfficeId) {
            const responseOk = await this.modalService.open(ModalConfirmComponent, {
                acceptCaption: 'Confirm',
                cancelCaption: 'Cancel',
                content:
                    "You're about to set a new main office for this company. This will replace your current main office with the selected one. Do you wish to continue?",
                title: 'New Main Office'
            });

            if (!responseOk) return;
        }

        const oldMainOfficeId = company.mainOfficeId;

        await this.awaiter.awaitAction(
            'Setting as Main Office',
            async () => {
                await this.companyBuildingService.setAsMain({
                    companyId: companyBuilding.companyId,
                    buildingId: companyBuilding.buildingId,
                    line2: companyBuilding.line2
                } as CompanyBuildingView);

                this.setMainOfficeInGrid(companyBuilding.buildingId, oldMainOfficeId);
                this.handleCompanyChange.emit();
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async unsetAsMainOffice(companyBuilding: CompanyBuildingView): Promise<void> {
        if (!companyBuilding) return;

        await this.awaiter.awaitAction(
            'Unsetting as Main Office',
            async () => {
                await this.companyBuildingService.unsetAsMain(companyBuilding);
                this.unsetMainOfficeInGrid(companyBuilding.buildingId);
                this.handleCompanyChange.emit();
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async onSelectedBuilding(building: BuildingView): Promise<void> {
        const companyId = this.company()?.id;
        if (!building || !companyId) return;

        try {
            const buildingSaved = await this.companyBuildingService.save({
                companyId: companyId,
                buildingId: building.id
            } as CompanyBuildingView);

            if (buildingSaved) {
                this.tabCounterChange.emit();
                const gridApi = this.gridService.getGridApi();

                let updated = false;

                if (gridApi?.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        add: [buildingSaved]
                    });

                    updated = transactionResult?.status === 'Applied';
                }

                if (!updated) {
                    this.refreshGridView();
                }

                this.noResults.set(false);
            }
        } catch (error) {
            console.error(error);
            this.snackbarNotificationService.error('An error occurred while trying to save the building');
        }
    }

    async removeItem(companyBuilding: CompanyBuildingView): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove building',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this building from the building list?',
            title: 'Remove building'
        });

        if (!responseOk) return;

        const { buildingId } = companyBuilding;
        const companyId = this.company()?.id ?? companyBuilding.companyId;
        if (!buildingId || !companyId) return;

        await this.awaiter.awaitAction(
            'Removing building',
            async () => {
                await this.companyBuildingService.remove({
                    id: 0,
                    isMain: companyBuilding.isMain,
                    companyId: companyId,
                    buildingId: buildingId
                } as CompanyBuildingView);

                this.tabCounterChange.emit();
                if (companyBuilding.isMain) {
                    this.handleCompanyChange.emit();
                }

                const gridApi = this.gridService.getGridApi();
                let removed = false;

                if (gridApi) {
                    const node = gridApi.getRowNode(buildingId.toString());

                    if (node?.data && gridApi.applyServerSideTransaction) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            remove: [node.data]
                        });
                        removed = transactionResult?.status === 'Applied';
                    }

                    if (removed) {
                        this.noResults.set(gridApi.getDisplayedRowCount() === 0);
                    }
                }

                if (!removed) {
                    this.refreshGridView();
                }
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private unsetMainOfficeInGrid(buildingId?: number): void {
        if (!buildingId) return;

        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        const node = gridApi.getRowNode(buildingId.toString());
        if (node?.data) {
            node.data.isMain = false;
            gridApi.redrawRows({ rowNodes: [node] });
        }
    }

    private setMainOfficeInGrid(buildingId?: number, oldMainOfficeId?: number | null): void {
        if (!buildingId) return;

        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        const rowsToUpdate: any[] = [];

        if (oldMainOfficeId && oldMainOfficeId !== buildingId) {
            const oldMainNode = gridApi.getRowNode(oldMainOfficeId.toString());
            if (oldMainNode?.data) {
                oldMainNode.data.isMain = false;
                rowsToUpdate.push(oldMainNode);
            }
        }

        const newMainNode = gridApi.getRowNode(buildingId.toString());
        if (newMainNode?.data) {
            newMainNode.data.isMain = true;
            rowsToUpdate.push(newMainNode);
        }

        if (rowsToUpdate.length > 0) {
            gridApi.redrawRows({ rowNodes: rowsToUpdate });
        }
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Edit Address Line 2',
                action: (building: CompanyBuildingView) => this.editAddress(building)
            },
            {
                label: 'Set as Main Office',
                action: (building: CompanyBuildingView) => this.setAsMainOffice(building),
                show: (building: CompanyBuildingView) => !building.isMain
            },
            {
                label: 'Unset as Main Office',
                action: (building: CompanyBuildingView) => this.unsetAsMainOffice(building),
                show: (building: CompanyBuildingView) => building.isMain
            },
            {
                label: 'Remove',
                action: (building: CompanyBuildingView) => this.removeItem(building)
            }
        ];

        this.gridColumns = COMPANY_BUILDINGS_GRID_COLUMNS.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchBuildingsForCompanyParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;

        const companyId = this.company()?.id;
        if (!companyId) {
            throw new Error('Company is required to search buildings for company.');
        }

        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            companyId
        } as SearchBuildingsForCompanyParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.buildingsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.buildingService.searchBuildingsForCompany(searchParams);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching buildings:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }
}
